/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/next";
import type { FlaggedPromptResponse } from "@packages/sdk";
import { getFullName, useDate } from "@packages/sdk";
import type { MaskProps, ThemeType } from "@packages/ui";
import {
  Avatar,
  constantSemanticColors,
  radius,
  ScrollArea,
  spacing,
  stroke,
  Text,
} from "@packages/ui";
import type { HTMLAttributes } from "react";

const baseStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.m,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  display: "flex",
  flexDirection: "column",
  padding: spacing.m,
});

const headerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
  marginBottom: spacing.ms,
};

const headerTextStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.t,
};

const headerTextTimeStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsMedium,
});

const textStyles: Interpolation = {
  color: constantSemanticColors.onColorHighest,
};

const textContainerStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
};

const textPromptStyles: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
};

const scrollAreaViewportStyles = (props: {
  backgroundColor: string;
}): Interpolation => ({
  backgroundColor: props.backgroundColor,
  borderRadius: radius.m,
  maxHeight: 320,
  padding: spacing.l,
});

const scrollAreaViewportMaskBottomStyles: Interpolation = {
  borderBottomLeftRadius: radius.m,
  borderBottomRightRadius: radius.m,
};

const scrollAreaViewportMaskTopStyles: Interpolation = {
  borderTopLeftRadius: radius.m,
  borderTopRightRadius: radius.m,
};

type ReportedCardPromptResponseProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  data: FlaggedPromptResponse;
};

export const ReportedCardPromptResponse = ({
  data,
  ...props
}: ReportedCardPromptResponseProps) => {
  const { getDateFormatDifferenceNow } = useDate();

  return (
    <div css={baseStyles} {...props}>
      <div css={headerStyles}>
        <Avatar imgSrc={data.user.image_url} size={40} />
        <div css={headerTextStyles}>
          <Text size="xl" type="detail">
            <strong>
              {getFullName({
                firstName: data?.user.name,
                lastName: data?.user.last_name,
              })}
            </strong>
            &nbsp;
            <I18nMessage message="responded_to_a_discussion" />
          </Text>
          <Text size="l" css={headerTextTimeStyles} type="detail">
            {getDateFormatDifferenceNow({ date: new Date(data.created_at) })}
          </Text>
        </div>
      </div>
      <ScrollArea
        maskBottom
        maskBottomCSS={[scrollAreaViewportMaskBottomStyles]}
        masksColor={data.prayer?.images?.color_hex as MaskProps["color"]}
        maskTop
        maskTopCSS={[scrollAreaViewportMaskTopStyles]}
        viewportCSS={[
          scrollAreaViewportStyles({
            backgroundColor: data.prayer?.images
              ?.color_hex as MaskProps["color"],
          }),
        ]}
      >
        <div css={textContainerStyles}>
          <Text as="span" size="l" css={textPromptStyles} type="quote">
            {data.prayer?.prompt}
          </Text>
          <Text as="p" size="l" css={textStyles} type="body">
            {data.text}
          </Text>
        </div>
      </ScrollArea>
    </div>
  );
};

ReportedCardPromptResponse.displayName = "ReportedCardPromptResponse";
